import React, { useState } from 'react';
import ReactDOM from 'react-dom/client';
import { HiX, HiDownload, HiCheck } from 'react-icons/hi';
import ExcelJS from 'exceljs';
import jsPDF from 'jspdf';
// Import components for off-screen rendering
import ResumoTab from './ResumoTab';
import StatisticsTab from './StatisticsTab';
import DistributionsTab from './DistributionsTab';
import SimulationTab from './SimulationTab';
import { formatDataDeDias } from '../utils/dateFromDays';

const ExportModal = ({ results, appliedFilters, activeTab, dataReferencia, onClose }) => {
    const [selectedFormat, setSelectedFormat] = useState('xlsx');
    const [selectedSections, setSelectedSections] = useState([activeTab || 'resumo']);
    const [isExporting, setIsExporting] = useState(false);
    const [exportSuccess, setExportSuccess] = useState(false);

    const formats = [
        { id: 'xlsx', label: 'Excel', description: 'Excel Spreadsheet' },
        { id: 'json', label: 'JSON', description: 'JavaScript Object Notation' },
        { id: 'pdf', label: 'PDF', description: 'Portable Document Format with charts' }
    ];

    const sections = [
        { id: 'resumo', label: 'Resumo', description: 'Tabela de dados e heatmap TRL/CRL' },
        { id: 'statistics', label: 'Estatística descritiva', description: 'Estatística descritiva' },
        { id: 'distributions', label: 'Distribuições', description: 'Distribuições Empíricas/Ajustadas e Random Forest' },
        { id: 'simulation', label: 'Simulação', description: 'Resultados de simulação TRL/CRL' }
    ];

    const handleSectionChange = (sectionId) => {
        setSelectedSections(prev =>
            prev.includes(sectionId)
                ? prev.filter(id => id !== sectionId)
                : [...prev, sectionId]
        );
    };

    const formatAppliedFilters = () => {
        if (!appliedFilters) return null;

        // Use the same format as backend expects (same logic as convertFiltersToBackendFormat)
        const backendFormatFilters = {};

        // Convert innovation degree checkboxes to array
        const selectedInnovationDegrees = Object.entries(appliedFilters.innovationDegree || {})
            .filter(([_, selected]) => selected)
            .map(([level]) => parseInt(level));

        if (selectedInnovationDegrees.length > 0) {
            backendFormatFilters.grauInovacao = selectedInnovationDegrees;
        }

        // Convert arrays (if they have selections)
        if (appliedFilters.typology?.length > 0) {
            backendFormatFilters.tipologia = appliedFilters.typology;
        }

        if (appliedFilters.status?.length > 0) {
            backendFormatFilters.status = appliedFilters.status;
        }

        if (appliedFilters.portfolio?.length > 0) {
            backendFormatFilters.portfolio = appliedFilters.portfolio;
        }

        if (appliedFilters.technologicalInitiative?.length > 0) {
            backendFormatFilters.iniciativaTecnologica = appliedFilters.technologicalInitiative;
        }

        if (appliedFilters.category?.length > 0) {
            backendFormatFilters.categoria = appliedFilters.category;
        }

        // Add date filters if selected
        if (appliedFilters.dataMin && appliedFilters.dataMin.trim()) {
            backendFormatFilters.dataMin = appliedFilters.dataMin;
        }

        if (appliedFilters.dataMax && appliedFilters.dataMax.trim()) {
            backendFormatFilters.dataMax = appliedFilters.dataMax;
        }

        // Parameters object - exactly like backend expects
        const parametros = {
            'TRL': {"inicial": 1, "final": 9},
            'CRL': {"inicial": 1, "final": 9},
            'tempo': false,
            'custo': false
        };

        // Add TRL/CRL ranges if they're different from defaults
        if (appliedFilters.trlInitial !== 1 || appliedFilters.trlFinal !== 9) {
            parametros.TRL = {
                "inicial": appliedFilters.trlInitial,
                "final": appliedFilters.trlFinal
            };
        }

        if (appliedFilters.crlInitial !== 1 || appliedFilters.crlFinal !== 9) {
            parametros.CRL = {
                "inicial": appliedFilters.crlInitial,
                "final": appliedFilters.crlFinal
            };
        }

        backendFormatFilters.parametros = parametros;

        return backendFormatFilters;
    };

    const prepareExportData = () => {
        const exportData = {};

        // Add applied filters information
        const filtersInfo = formatAppliedFilters();
        if (filtersInfo) {
            exportData.filtrosAplicados = filtersInfo;
        }

        if (selectedSections.includes('resumo') && results.Resumo) {
            exportData.Resumo = {
                dados: results.Resumo.dados,
                trlcrl: results.Resumo.trlcrl,
                trlcrlIndiv: results.Resumo.trlcrlIndiv
            };
        }

        if (selectedSections.includes('statistics') && results.Estatisticas) {
            if (results.Estatisticas.estList && typeof results.Estatisticas.estList === 'object') {
                const estListArray = Object.values(results.Estatisticas.estList).filter(item => item.Contagem > 0);
                exportData.Estatisticas = {
                    estList: estListArray
                };
            }
        }

        if (selectedSections.includes('distributions') && results.Distribuicoes) {
            exportData.Distribuicoes = results.Distribuicoes;
        }

        if (selectedSections.includes('simulation') && results.Simulador) {
            exportData.Simulador = results.Simulador;
        }

        return exportData;
    };

    const sanitizeValue = (value) => {
        if (value === null || value === undefined) return '';
        if (typeof value === 'number') {
            if (isNaN(value) || !isFinite(value)) return '';
        }
        return value;
    };

    const convertToXLSX = async (data) => {
        const workbook = new ExcelJS.Workbook();

        // Add Filters Information Sheet
        const filtersInfo = formatAppliedFilters();
        if (filtersInfo) {
            const filtersWs = workbook.addWorksheet('Filtros Aplicados');

            // Add title
            filtersWs.addRow(['Filtros Aplicados na Análise']);
            filtersWs.getRow(1).font = { bold: true, size: 16 };
            filtersWs.addRow([]);

            // Add timestamp
            // const timestamp = new Date().toLocaleString('pt-BR');
            // filtersWs.addRow([`Exportado em: ${timestamp}`]);
            // filtersWs.getRow(3).font = { italic: true };
            // filtersWs.addRow([]);

            let currentRow = 5;

            // Parameters (using backend format)
            filtersWs.addRow(['PARÂMETROS', '']);
            filtersWs.getRow(currentRow).font = { bold: true, size: 14 };
            currentRow++;

            // TRL object
            filtersWs.addRow(['TRL', JSON.stringify(filtersInfo.parametros.TRL)]);
            filtersWs.addRow(['  TRL inicial', filtersInfo.parametros.TRL.inicial]);
            filtersWs.addRow(['  TRL final', filtersInfo.parametros.TRL.final]);

            // CRL object
            filtersWs.addRow(['CRL', JSON.stringify(filtersInfo.parametros.CRL)]);
            filtersWs.addRow(['  CRL inicial', filtersInfo.parametros.CRL.inicial]);
            filtersWs.addRow(['  CRL final', filtersInfo.parametros.CRL.final]);

            filtersWs.addRow(['Tempo', filtersInfo.parametros.tempo]);
            filtersWs.addRow(['Custo', filtersInfo.parametros.custo]);
            filtersWs.addRow(['']); // Empty row for spacing
            currentRow += 9;

            // Innovation Degree
            if (filtersInfo.grauInovacao) {
                filtersWs.addRow(['grauInovacao', JSON.stringify(filtersInfo.grauInovacao)]);
                filtersInfo.grauInovacao.forEach(degree => {
                    filtersWs.addRow(['  ' + degree, '']);
                });
                filtersWs.addRow(['']); // Empty row for spacing
                currentRow += filtersInfo.grauInovacao.length + 2;
            }

            // Typology
            if (filtersInfo.tipologia) {
                filtersWs.addRow(['tipologia', JSON.stringify(filtersInfo.tipologia)]);
                filtersInfo.tipologia.forEach(type => {
                    filtersWs.addRow(['  ' + type, '']);
                });
                filtersWs.addRow(['']); // Empty row for spacing
                currentRow += filtersInfo.tipologia.length + 2;
            }

            // Status
            if (filtersInfo.status) {
                filtersWs.addRow(['status', JSON.stringify(filtersInfo.status)]);
                filtersInfo.status.forEach(status => {
                    filtersWs.addRow(['  ' + status, '']);
                });
                filtersWs.addRow(['']); // Empty row for spacing
                currentRow += filtersInfo.status.length + 2;
            }

            // Portfolio
            if (filtersInfo.portfolio) {
                filtersWs.addRow(['portfolio', JSON.stringify(filtersInfo.portfolio)]);
                filtersInfo.portfolio.forEach(portfolio => {
                    filtersWs.addRow(['  ' + portfolio, '']);
                });
                filtersWs.addRow(['']); // Empty row for spacing
                currentRow += filtersInfo.portfolio.length + 2;
            }

            // Technological Initiative
            if (filtersInfo.iniciativaTecnologica) {
                filtersWs.addRow(['iniciativaTecnologica', JSON.stringify(filtersInfo.iniciativaTecnologica)]);
                filtersInfo.iniciativaTecnologica.forEach(initiative => {
                    filtersWs.addRow(['  ' + initiative, '']);
                });
                filtersWs.addRow(['']); // Empty row for spacing
                currentRow += filtersInfo.iniciativaTecnologica.length + 2;
            }

            // Date filters (backend format)
            if (filtersInfo.dataMin) {
                filtersWs.addRow(['dataMin', filtersInfo.dataMin]);
            }

            if (filtersInfo.dataMax) {
                filtersWs.addRow(['dataMax', filtersInfo.dataMax]);
            }

            // Adjust column widths and formatting
            filtersWs.getColumn(1).width = 30;
            filtersWs.getColumn(2).width = 50;

            // Apply formatting - make main field names bold
            let rowIndex = 5;
            filtersWs.getRow(rowIndex).font = { bold: true, size: 14 }; // PARÂMETROS title

            // Make main filter field names bold
            let currentRowIndex = rowIndex + 9; // After parameters section

            if (filtersInfo.grauInovacao) {
                filtersWs.getRow(currentRowIndex).font = { bold: true };
                currentRowIndex += filtersInfo.grauInovacao.length + 2;
            }

            if (filtersInfo.tipologia) {
                filtersWs.getRow(currentRowIndex).font = { bold: true };
                currentRowIndex += filtersInfo.tipologia.length + 2;
            }

            if (filtersInfo.status) {
                filtersWs.getRow(currentRowIndex).font = { bold: true };
                currentRowIndex += filtersInfo.status.length + 2;
            }

            if (filtersInfo.portfolio) {
                filtersWs.getRow(currentRowIndex).font = { bold: true };
                currentRowIndex += filtersInfo.portfolio.length + 2;
            }

            if (filtersInfo.iniciativaTecnologica) {
                filtersWs.getRow(currentRowIndex).font = { bold: true };
                currentRowIndex += filtersInfo.iniciativaTecnologica.length + 2;
            }
        }

        // Resumo - Tabela de dados
        if (data.Resumo && data.Resumo.dados) {
            const resumoData = data.Resumo.dados;
            if (resumoData.columns && resumoData.data) {
                const ws = workbook.addWorksheet('Resumo - Dados');
                ws.addRow(resumoData.columns);
                resumoData.data.forEach(row => {
                    ws.addRow(row.map(cell => sanitizeValue(cell)));
                });

                ws.getRow(1).font = { bold: true };
                ws.columns.forEach(column => { column.width = 15; });
            }

            // Resumo - TRL/CRL Heatmap
            if (data.Resumo.trlcrl) {
                const ws = workbook.addWorksheet('Resumo - Heatmap');
                const trlcrl = data.Resumo.trlcrl;
                const trlKeys = Object.keys(trlcrl).sort((a, b) => parseInt(a) - parseInt(b));

                if (trlKeys.length > 0) {
                    const crlKeys = Object.keys(trlcrl[trlKeys[0]]).sort((a, b) => parseInt(a) - parseInt(b));
                    ws.addRow(['TRL/CRL', ...crlKeys]);

                    trlKeys.forEach(trl => {
                        const row = [trl];
                        crlKeys.forEach(crl => {
                            row.push(sanitizeValue(trlcrl[trl][crl]) || 0);
                        });
                        ws.addRow(row);
                    });

                    ws.getRow(1).font = { bold: true };
                }
            }
        }

        // Estatísticas Descritivas
        if (data.Estatisticas && data.Estatisticas.estList) {
            const stats = data.Estatisticas.estList;

            if (Array.isArray(stats) && stats.length > 0) {
                const ws = workbook.addWorksheet('Estatísticas');

                const headers = [
                    'Transição',
                    'Contagem',
                    'Média',
                    'Mínimo',
                    'Máximo',
                    'Desvio Padrão',
                    'Coef. Variação (%)',
                    'Percentil 25%',
                    'Percentil 50%',
                    'Percentil 75%'
                ];

                ws.addRow(headers);

                stats.forEach(stat => {
                    ws.addRow([
                        sanitizeValue(stat.Coluna),
                        sanitizeValue(stat.Contagem),
                        sanitizeValue(stat.Média),
                        sanitizeValue(stat.Mínimo),
                        sanitizeValue(stat.Máximo),
                        sanitizeValue(stat['Desvio Padrão']),
                        sanitizeValue(stat['Coef. Variação (%)']),
                        sanitizeValue(stat['Percentil 25%']),
                        sanitizeValue(stat['Percentil 50%']),
                        sanitizeValue(stat['Percentil 75%'])
                    ]);
                });

                ws.getRow(1).font = { bold: true };
                ws.columns.forEach(column => { column.width = 18; });
            }
        }

        // Distribuições - Ajustadas
        if (data.Distribuicoes && data.Distribuicoes.distAjustadas) {
            const ws = workbook.addWorksheet('Dist. Ajustadas');
            const distAjustadas = data.Distribuicoes.distAjustadas;

            ws.addRow(['Transição', 'Distribuição', 'Parâmetros', 'LogLik', 'AIC', 'BIC', 'KS Stat', 'KS p-valor', 'Shapiro Stat', 'Shapiro p-valor']);

            Object.keys(distAjustadas).forEach(key => {
                const dist = distAjustadas[key].distMelhor;
                ws.addRow([
                    key,
                    dist.distribuicao,
                    dist.params.join(', '),
                    sanitizeValue(dist.loglik),
                    sanitizeValue(dist.aic),
                    sanitizeValue(dist.bic),
                    sanitizeValue(dist.ks_stat),
                    sanitizeValue(dist.ks_p),
                    sanitizeValue(dist.shapiro_stat),
                    sanitizeValue(dist.shapiro_p)
                ]);
            });

            ws.getRow(1).font = { bold: true };
            ws.columns.forEach(column => { column.width = 15; });
        }

        // Distribuições - Empíricas
        if (data.Distribuicoes && data.Distribuicoes.distEmpiricas) {
            const ws = workbook.addWorksheet('Dist. Empíricas');
            const distEmp = data.Distribuicoes.distEmpiricas;

            ws.addRow(['Transição', 'Média', 'P10', 'P50', 'P75', 'P90']);

            if (distEmp.dados) {
                Object.keys(distEmp.dados).forEach(key => {
                    ws.addRow([
                        key,
                        sanitizeValue(distEmp.media?.[key]),
                        sanitizeValue(distEmp.p10?.[key]),
                        sanitizeValue(distEmp.p50?.[key]),
                        sanitizeValue(distEmp.p75?.[key]),
                        sanitizeValue(distEmp.p90?.[key])
                    ]);
                });
            }

            ws.getRow(1).font = { bold: true };
            ws.columns.forEach(column => { column.width = 15; });
        }

        // Distribuições - Random Forest
        if (data.Distribuicoes && data.Distribuicoes.distRndForest) {
            const ws = workbook.addWorksheet('Dist. Random Forest');
            const rfData = data.Distribuicoes.distRndForest;

            ws.addRow(['Transição', 'Média', 'Desvio', 'P10', 'P50', 'P75', 'P90']);

            Object.keys(rfData).forEach(key => {
                const rf = rfData[key];
                ws.addRow([
                    key,
                    sanitizeValue(rf.media),
                    sanitizeValue(rf.desvio),
                    sanitizeValue(rf.p10),
                    sanitizeValue(rf.p50),
                    sanitizeValue(rf.p75),
                    sanitizeValue(rf.p90)
                ]);
            });

            ws.getRow(1).font = { bold: true };
            ws.columns.forEach(column => { column.width = 15; });
        }

        const hasDataRef = !!dataReferencia;
        const dataCols = hasDataRef
            ? ['Data Média', 'Data P10', 'Data P50', 'Data P75', 'Data P90']
            : [];

        // Simulação - TRL
        if (data.Simulador && data.Simulador.agregadoTRL) {
            const ws = workbook.addWorksheet('Simulação - TRL');
            const trl = data.Simulador.agregadoTRL;

            ws.addRow(['Transição TRL', 'Média', 'P10', 'P50', 'P75', 'P90', ...dataCols]);

            if (trl.simulacao && typeof trl.simulacao !== 'string') {
                const keys = Object.keys(trl.simulacao).sort((a, b) => Number(a) - Number(b));
                const ini = keys.length > 0 ? Number(keys[0]) : null;
                keys.forEach(key => {
                    ws.addRow([
                        `${ini}-${Number(key) + 1}`,
                        sanitizeValue(trl.media[key]),
                        sanitizeValue(trl.p10[key]),
                        sanitizeValue(trl.p50[key]),
                        sanitizeValue(trl.p75[key]),
                        sanitizeValue(trl.p90[key]),
                        ...(hasDataRef
                            ? [
                                formatDataDeDias(trl.media[key], dataReferencia) || '',
                                formatDataDeDias(trl.p10[key], dataReferencia) || '',
                                formatDataDeDias(trl.p50[key], dataReferencia) || '',
                                formatDataDeDias(trl.p75[key], dataReferencia) || '',
                                formatDataDeDias(trl.p90[key], dataReferencia) || ''
                            ]
                            : [])
                    ]);
                });
            }

            ws.getRow(1).font = { bold: true };
            ws.columns.forEach(column => { column.width = 15; });
        }

        // Simulação - CRL
        if (data.Simulador && data.Simulador.agregadoCRL) {
            const ws = workbook.addWorksheet('Simulação - CRL');
            const crl = data.Simulador.agregadoCRL;

            ws.addRow(['Transição CRL', 'Média', 'P10', 'P50', 'P75', 'P90', ...dataCols]);

            if (crl.simulacao && typeof crl.simulacao !== 'string') {
                const keys = Object.keys(crl.simulacao).sort((a, b) => Number(a) - Number(b));
                const ini = keys.length > 0 ? Number(keys[0]) : null;
                keys.forEach(key => {
                    ws.addRow([
                        `${ini}-${Number(key) + 1}`,
                        sanitizeValue(crl.media[key]),
                        sanitizeValue(crl.p10[key]),
                        sanitizeValue(crl.p50[key]),
                        sanitizeValue(crl.p75[key]),
                        sanitizeValue(crl.p90[key]),
                        ...(hasDataRef
                            ? [
                                formatDataDeDias(crl.media[key], dataReferencia) || '',
                                formatDataDeDias(crl.p10[key], dataReferencia) || '',
                                formatDataDeDias(crl.p50[key], dataReferencia) || '',
                                formatDataDeDias(crl.p75[key], dataReferencia) || '',
                                formatDataDeDias(crl.p90[key], dataReferencia) || ''
                            ]
                            : [])
                    ]);
                });
            }

            ws.getRow(1).font = { bold: true };
            ws.columns.forEach(column => { column.width = 15; });
        }

        return workbook;
    };

    // Helper function to convert chart data to buffer for Excel
    const chartToBuffer = async (chartData) => {
        if (!chartData || !chartData.dataUrl) {
            return null;
        }

        try {
            // Check if it's a data URL
            if (!chartData.dataUrl.startsWith('data:image/')) {
                return null;
            }

            // Convert data URL to buffer
            const response = await fetch(chartData.dataUrl);
            if (!response.ok) {
                return null;
            }

            const arrayBuffer = await response.arrayBuffer();
            if (arrayBuffer.byteLength === 0) {
                return null;
            }

            // In browser environment, we need to convert ArrayBuffer to Uint8Array for ExcelJS
            const uint8Array = new Uint8Array(arrayBuffer);
            return uint8Array;
        } catch (error) {
            console.error('Error converting chart to buffer:', error);
            return null;
        }
    };

    // Helper function to add chart to worksheet
    const addChartToWorksheet = async (workbook, chartWs, chart, title, currentRow) => {
        const imageBuffer = await chartToBuffer(chart);
        if (imageBuffer) {
            try {
                const imageId = workbook.addImage({
                    buffer: imageBuffer,
                    extension: 'png'
                });

                chartWs.addImage(imageId, {
                    tl: { col: 0, row: currentRow - 1 },
                    ext: { width: 600, height: 300 },
                    editAs: 'oneCell'
                });
                return true;
            } catch (imageError) {
                console.error('Error adding image to workbook:', imageError);
                return false;
            }
        }
        return false;
    };

    // Enhanced XLSX conversion with charts - builds sheets in tab order
    const convertToXLSXWithCharts = async (data) => {
        const workbook = new ExcelJS.Workbook();

        // === STEP 1: RENDER ALL CHARTS IN PARALLEL ===
        let resumoCharts = [];
        let statisticsCharts = [];
        let distributionChartsEmp = [];
        let distributionChartsAjust = [];
        let distributionChartsRF = [];
        let simulationCharts = [];

        const renderPromises = [];

        if (selectedSections.includes('resumo') && data.Resumo) {
            renderPromises.push(
                renderComponentOffScreen(ResumoTab, { Resumo: data.Resumo }, 'TRL/CRL')
                    .then(charts => { resumoCharts = charts; })
                    .catch(e => console.error('Error rendering resumo charts:', e))
            );
        }
        if (selectedSections.includes('statistics') && data.Estatisticas) {
            renderPromises.push(
                renderComponentOffScreen(StatisticsTab, { Estatisticas: data.Estatisticas }, 'GRAFICOS')
                    .then(charts => { statisticsCharts = charts; })
                    .catch(e => console.error('Error rendering statistics charts:', e))
            );
        }
        if (selectedSections.includes('distributions') && data.Distribuicoes) {
            renderPromises.push(
                renderComponentOffScreen(DistributionsTab, { Distribuicoes: data.Distribuicoes }, 'EMPÍRICAS')
                    .then(charts => { distributionChartsEmp = charts; })
                    .catch(e => console.error('Error rendering dist empirical charts:', e))
            );
            renderPromises.push(
                renderComponentOffScreen(DistributionsTab, { Distribuicoes: data.Distribuicoes }, 'AJUSTADAS')
                    .then(charts => { distributionChartsAjust = charts; })
                    .catch(e => console.error('Error rendering dist fitted charts:', e))
            );
            renderPromises.push(
                renderComponentOffScreen(DistributionsTab, { Distribuicoes: data.Distribuicoes }, 'RANDOM FOREST')
                    .then(charts => { distributionChartsRF = charts; })
                    .catch(e => console.error('Error rendering dist RF charts:', e))
            );
        }
        if (selectedSections.includes('simulation') && data.Simulador) {
            renderPromises.push(
                renderComponentOffScreen(SimulationTab, { Simulador: data.Simulador }, 'GRAFICOS', { dataReferencia })
                    .then(charts => { simulationCharts = charts; })
                    .catch(e => console.error('Error rendering simulation charts:', e))
            );
        }

        try {
            await Promise.all(renderPromises);
        } catch (error) {
            console.error('Error rendering charts in parallel:', error);
        }

        // === STEP 2: BUILD WORKBOOK IN TAB ORDER ===

        // --- Filtros Aplicados ---
        const filtersInfo = formatAppliedFilters();
        if (filtersInfo) {
            const filtersWs = workbook.addWorksheet('Filtros Aplicados');

            filtersWs.addRow(['Filtros Aplicados na Análise']);
            filtersWs.getRow(1).font = { bold: true, size: 16 };
            filtersWs.addRow([]);

            let currentRow = 5;
            filtersWs.addRow(['PARÂMETROS', '']);
            filtersWs.getRow(currentRow).font = { bold: true, size: 14 };
            currentRow++;

            filtersWs.addRow(['TRL', JSON.stringify(filtersInfo.parametros.TRL)]);
            filtersWs.addRow(['  TRL inicial', filtersInfo.parametros.TRL.inicial]);
            filtersWs.addRow(['  TRL final', filtersInfo.parametros.TRL.final]);
            filtersWs.addRow(['CRL', JSON.stringify(filtersInfo.parametros.CRL)]);
            filtersWs.addRow(['  CRL inicial', filtersInfo.parametros.CRL.inicial]);
            filtersWs.addRow(['  CRL final', filtersInfo.parametros.CRL.final]);
            filtersWs.addRow(['Tempo', filtersInfo.parametros.tempo]);
            filtersWs.addRow(['Custo', filtersInfo.parametros.custo]);
            filtersWs.addRow(['']);
            currentRow += 9;

            if (filtersInfo.grauInovacao) {
                filtersWs.addRow(['grauInovacao', JSON.stringify(filtersInfo.grauInovacao)]);
                filtersInfo.grauInovacao.forEach(degree => { filtersWs.addRow(['  ' + degree, '']); });
                filtersWs.addRow(['']);
                currentRow += filtersInfo.grauInovacao.length + 2;
            }
            if (filtersInfo.tipologia) {
                filtersWs.addRow(['tipologia', JSON.stringify(filtersInfo.tipologia)]);
                filtersInfo.tipologia.forEach(type => { filtersWs.addRow(['  ' + type, '']); });
                filtersWs.addRow(['']);
                currentRow += filtersInfo.tipologia.length + 2;
            }
            if (filtersInfo.status) {
                filtersWs.addRow(['status', JSON.stringify(filtersInfo.status)]);
                filtersInfo.status.forEach(status => { filtersWs.addRow(['  ' + status, '']); });
                filtersWs.addRow(['']);
                currentRow += filtersInfo.status.length + 2;
            }
            if (filtersInfo.portfolio) {
                filtersWs.addRow(['portfolio', JSON.stringify(filtersInfo.portfolio)]);
                filtersInfo.portfolio.forEach(portfolio => { filtersWs.addRow(['  ' + portfolio, '']); });
                filtersWs.addRow(['']);
                currentRow += filtersInfo.portfolio.length + 2;
            }
            if (filtersInfo.iniciativaTecnologica) {
                filtersWs.addRow(['iniciativaTecnologica', JSON.stringify(filtersInfo.iniciativaTecnologica)]);
                filtersInfo.iniciativaTecnologica.forEach(initiative => { filtersWs.addRow(['  ' + initiative, '']); });
                filtersWs.addRow(['']);
                currentRow += filtersInfo.iniciativaTecnologica.length + 2;
            }
            if (filtersInfo.dataMin) { filtersWs.addRow(['dataMin', filtersInfo.dataMin]); }
            if (filtersInfo.dataMax) { filtersWs.addRow(['dataMax', filtersInfo.dataMax]); }

            filtersWs.getColumn(1).width = 30;
            filtersWs.getColumn(2).width = 50;

            let rowIndex = 5;
            filtersWs.getRow(rowIndex).font = { bold: true, size: 14 };
            let currentRowIndex = rowIndex + 9;
            if (filtersInfo.grauInovacao) { filtersWs.getRow(currentRowIndex).font = { bold: true }; currentRowIndex += filtersInfo.grauInovacao.length + 2; }
            if (filtersInfo.tipologia) { filtersWs.getRow(currentRowIndex).font = { bold: true }; currentRowIndex += filtersInfo.tipologia.length + 2; }
            if (filtersInfo.status) { filtersWs.getRow(currentRowIndex).font = { bold: true }; currentRowIndex += filtersInfo.status.length + 2; }
            if (filtersInfo.portfolio) { filtersWs.getRow(currentRowIndex).font = { bold: true }; currentRowIndex += filtersInfo.portfolio.length + 2; }
            if (filtersInfo.iniciativaTecnologica) { filtersWs.getRow(currentRowIndex).font = { bold: true }; currentRowIndex += filtersInfo.iniciativaTecnologica.length + 2; }
        }

        // --- ABA 1: RESUMO ---
        if (data.Resumo) {
            // Resumo - Dados
            if (data.Resumo.dados) {
                const resumoData = data.Resumo.dados;
                if (resumoData.columns && resumoData.data) {
                    const ws = workbook.addWorksheet('Resumo - Dados');
                    ws.addRow(resumoData.columns);
                    resumoData.data.forEach(row => {
                        ws.addRow(row.map(cell => sanitizeValue(cell)));
                    });
                    ws.getRow(1).font = { bold: true };
                    ws.columns.forEach(column => { column.width = 15; });
                }
            }

            // Resumo - Heatmap
            if (data.Resumo.trlcrl) {
                const ws = workbook.addWorksheet('Resumo - Heatmap');
                const trlcrl = data.Resumo.trlcrl;
                const trlKeys = Object.keys(trlcrl).sort((a, b) => parseInt(a) - parseInt(b));

                if (trlKeys.length > 0) {
                    const crlKeys = Object.keys(trlcrl[trlKeys[0]]).sort((a, b) => parseInt(a) - parseInt(b));

                    // Linha 1: cabeçalho com labels CRL nas colunas
                    ws.addRow(['', ...crlKeys.map(crl => `CRL ${crl}`)]);
                    const headerRow = ws.getRow(1);
                    headerRow.font = { bold: true };
                    headerRow.eachCell((cell, colNumber) => {
                        if (colNumber > 1) {
                            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFB8CCE4' } };
                            cell.alignment = { horizontal: 'center', vertical: 'middle' };
                        }
                    });

                    // Linhas de dados: cada linha é um nível TRL
                    trlKeys.forEach((trl, rowIdx) => {
                        const row = [`TRL ${trl}`];
                        crlKeys.forEach(crl => {
                            row.push(sanitizeValue(trlcrl[trl][crl]) || 0);
                        });
                        ws.addRow(row);
                        const dataRow = ws.getRow(2 + rowIdx);
                        const trlCell = dataRow.getCell(1);
                        trlCell.font = { bold: true };
                        trlCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD9E1F2' } };
                        trlCell.alignment = { horizontal: 'center', vertical: 'middle' };
                    });

                    ws.getColumn(1).width = 10;
                    crlKeys.forEach((_, i) => { ws.getColumn(i + 2).width = 10; });
                }
            }

            // Resumo - Gráficos
            if (resumoCharts.length > 0) {
                const chartWs = workbook.addWorksheet('Resumo - Gráficos');
                let currentRow = 2;
                for (const [index, chart] of resumoCharts.entries()) {
                    if (chart) {
                        const title = chart.title || `Resumo - Gráfico ${index + 1}`;
                        chartWs.getCell(`A${currentRow}`).value = title;
                        chartWs.getCell(`A${currentRow}`).font = { bold: true, size: 14 };
                        currentRow += 2;
                        const imageAdded = await addChartToWorksheet(workbook, chartWs, chart, title, currentRow);
                        if (imageAdded) currentRow += 20;
                    }
                }
                chartWs.getColumn(1).width = 80;
            }
        }

        // --- ABA 2: ESTATÍSTICAS ---
        if (data.Estatisticas && data.Estatisticas.estList) {
            const stats = data.Estatisticas.estList;
            if (Array.isArray(stats) && stats.length > 0) {
                const ws = workbook.addWorksheet('Estatísticas');
                const headers = [
                    'Transição', 'Contagem', 'Média', 'Mínimo', 'Máximo',
                    'Desvio Padrão', 'Coef. Variação (%)',
                    'Percentil 25%', 'Percentil 50%', 'Percentil 75%'
                ];
                ws.addRow(headers);
                stats.forEach(stat => {
                    ws.addRow([
                        sanitizeValue(stat.Coluna),
                        sanitizeValue(stat.Contagem),
                        sanitizeValue(stat.Média),
                        sanitizeValue(stat.Mínimo),
                        sanitizeValue(stat.Máximo),
                        sanitizeValue(stat['Desvio Padrão']),
                        sanitizeValue(stat['Coef. Variação (%)']),
                        sanitizeValue(stat['Percentil 25%']),
                        sanitizeValue(stat['Percentil 50%']),
                        sanitizeValue(stat['Percentil 75%'])
                    ]);
                });
                ws.getRow(1).font = { bold: true };
                ws.columns.forEach(column => { column.width = 18; });
            }

            if (statisticsCharts.length > 0) {
                const chartWs = workbook.addWorksheet('Estatísticas - Gráficos');
                let currentRow = 2;
                for (const [index, chart] of statisticsCharts.entries()) {
                    if (chart) {
                        const title = chart.title || `Estatística ${index + 1}`;
                        chartWs.getCell(`A${currentRow}`).value = title;
                        chartWs.getCell(`A${currentRow}`).font = { bold: true, size: 14 };
                        currentRow += 2;
                        const imageAdded = await addChartToWorksheet(workbook, chartWs, chart, title, currentRow);
                        if (imageAdded) currentRow += 20;
                    }
                }
                chartWs.getColumn(1).width = 80;
            }
        }

        // --- ABA 3: DISTRIBUIÇÕES ---
        if (data.Distribuicoes) {
            if (data.Distribuicoes.distAjustadas) {
                const ws = workbook.addWorksheet('Dist. Ajustadas');
                const distAjustadas = data.Distribuicoes.distAjustadas;
                ws.addRow(['Transição', 'Distribuição', 'Parâmetros', 'LogLik', 'AIC', 'BIC', 'KS Stat', 'KS p-valor', 'Shapiro Stat', 'Shapiro p-valor']);
                Object.keys(distAjustadas).forEach(key => {
                    const dist = distAjustadas[key].distMelhor;
                    ws.addRow([
                        key, dist.distribuicao, dist.params.join(', '),
                        sanitizeValue(dist.loglik), sanitizeValue(dist.aic), sanitizeValue(dist.bic),
                        sanitizeValue(dist.ks_stat), sanitizeValue(dist.ks_p),
                        sanitizeValue(dist.shapiro_stat), sanitizeValue(dist.shapiro_p)
                    ]);
                });
                ws.getRow(1).font = { bold: true };
                ws.columns.forEach(column => { column.width = 15; });
            }

            if (data.Distribuicoes.distEmpiricas) {
                const ws = workbook.addWorksheet('Dist. Empíricas');
                const distEmp = data.Distribuicoes.distEmpiricas;
                ws.addRow(['Transição', 'Média', 'P10', 'P50', 'P75', 'P90']);
                if (distEmp.dados) {
                    Object.keys(distEmp.dados).forEach(key => {
                        ws.addRow([
                            key,
                            sanitizeValue(distEmp.media?.[key]),
                            sanitizeValue(distEmp.p10?.[key]),
                            sanitizeValue(distEmp.p50?.[key]),
                            sanitizeValue(distEmp.p75?.[key]),
                            sanitizeValue(distEmp.p90?.[key])
                        ]);
                    });
                }
                ws.getRow(1).font = { bold: true };
                ws.columns.forEach(column => { column.width = 15; });
            }

            if (data.Distribuicoes.distRndForest) {
                const ws = workbook.addWorksheet('Dist. Random Forest');
                const rfData = data.Distribuicoes.distRndForest;
                ws.addRow(['Transição', 'Média', 'Desvio', 'P10', 'P50', 'P75', 'P90']);
                Object.keys(rfData).forEach(key => {
                    const rf = rfData[key];
                    ws.addRow([
                        key,
                        sanitizeValue(rf.media), sanitizeValue(rf.desvio),
                        sanitizeValue(rf.p10), sanitizeValue(rf.p50),
                        sanitizeValue(rf.p75), sanitizeValue(rf.p90)
                    ]);
                });
                ws.getRow(1).font = { bold: true };
                ws.columns.forEach(column => { column.width = 15; });
            }

            // Gráficos de distribuições
            const distChartSets = [
                { charts: distributionChartsEmp, sheetName: 'Distribuições - Empíricas', mode: 'EMPÍRICAS' },
                { charts: distributionChartsAjust, sheetName: 'Distribuições - Ajustadas', mode: 'AJUSTADAS' },
                { charts: distributionChartsRF, sheetName: 'Distribuições - RF', mode: 'RANDOM FOREST' }
            ];
            for (const { charts, sheetName, mode } of distChartSets) {
                if (charts.length > 0) {
                    const chartWs = workbook.addWorksheet(sheetName);
                    let currentRow = 2;
                    for (const [index, chart] of charts.entries()) {
                        if (chart) {
                            const title = chart.title || `${mode} ${index + 1}`;
                            chartWs.getCell(`A${currentRow}`).value = title;
                            chartWs.getCell(`A${currentRow}`).font = { bold: true, size: 14 };
                            currentRow += 2;
                            const imageBuffer = await chartToBuffer(chart);
                            if (imageBuffer) {
                                const imageId = workbook.addImage({ buffer: imageBuffer, extension: 'png' });
                                chartWs.addImage(imageId, {
                                    tl: { col: 0, row: currentRow - 1 },
                                    ext: { width: 600, height: 300 },
                                    editAs: 'oneCell'
                                });
                                currentRow += 20;
                            }
                        }
                    }
                    chartWs.getColumn(1).width = 80;
                }
            }
        }

        // --- ABA 4: SIMULAÇÃO ---
        if (data.Simulador) {
            const hasDataRefFull = !!dataReferencia;
            const dataColsFull = hasDataRefFull
                ? ['Data Média', 'Data P10', 'Data P50', 'Data P75', 'Data P90']
                : [];
            const dataRowFull = (obj, keys) => hasDataRefFull
                ? keys.map(k => formatDataDeDias(obj?.[k], dataReferencia) || '')
                : [];

            // Simulação - TRL
            if (data.Simulador.agregadoTRL) {
                const ws = workbook.addWorksheet('Simulação - TRL');
                const trl = data.Simulador.agregadoTRL;
                ws.addRow(['Transição TRL', 'Média', 'P10', 'P50', 'P75', 'P90', ...dataColsFull]);
                if (trl.simulacao && typeof trl.simulacao !== 'string') {
                    const keys = Object.keys(trl.simulacao).sort((a, b) => Number(a) - Number(b));
                    const ini = keys.length > 0 ? Number(keys[0]) : null;
                    keys.forEach(key => {
                        const stepObj = {
                            media: trl.media[key],
                            p10: trl.p10[key],
                            p50: trl.p50[key],
                            p75: trl.p75[key],
                            p90: trl.p90[key]
                        };
                        ws.addRow([
                            `${ini}-${Number(key) + 1}`,
                            sanitizeValue(stepObj.media),
                            sanitizeValue(stepObj.p10),
                            sanitizeValue(stepObj.p50),
                            sanitizeValue(stepObj.p75),
                            sanitizeValue(stepObj.p90),
                            ...dataRowFull(stepObj, ['media', 'p10', 'p50', 'p75', 'p90'])
                        ]);
                    });
                }
                ws.getRow(1).font = { bold: true };
                ws.columns.forEach(column => { column.width = 15; });
            }

            // Simulação - CRL
            if (data.Simulador.agregadoCRL) {
                const ws = workbook.addWorksheet('Simulação - CRL');
                const crl = data.Simulador.agregadoCRL;
                ws.addRow(['Transição CRL', 'Média', 'P10', 'P50', 'P75', 'P90', ...dataColsFull]);
                if (crl.simulacao && typeof crl.simulacao !== 'string') {
                    const keys = Object.keys(crl.simulacao).sort((a, b) => Number(a) - Number(b));
                    const ini = keys.length > 0 ? Number(keys[0]) : null;
                    keys.forEach(key => {
                        const stepObj = {
                            media: crl.media[key],
                            p10: crl.p10[key],
                            p50: crl.p50[key],
                            p75: crl.p75[key],
                            p90: crl.p90[key]
                        };
                        ws.addRow([
                            `${ini}-${Number(key) + 1}`,
                            sanitizeValue(stepObj.media),
                            sanitizeValue(stepObj.p10),
                            sanitizeValue(stepObj.p50),
                            sanitizeValue(stepObj.p75),
                            sanitizeValue(stepObj.p90),
                            ...dataRowFull(stepObj, ['media', 'p10', 'p50', 'p75', 'p90'])
                        ]);
                    });
                }
                ws.getRow(1).font = { bold: true };
                ws.columns.forEach(column => { column.width = 15; });
            }

            // Simulação - Conjunta (TRL + CRL)
            if (data.Simulador.agregadoConjunta) {
                const ws = workbook.addWorksheet('Simulação - Conjunta');
                const conjunta = data.Simulador.agregadoConjunta;
                ws.addRow(['Métrica', 'Média', 'P10', 'P50', 'P75', 'P90', ...dataColsFull]);
                ws.addRow([
                    'Duração Total (TRL + CRL)',
                    sanitizeValue(conjunta.duracaoMedia),
                    sanitizeValue(conjunta.duracaoP10),
                    sanitizeValue(conjunta.duracaoP50),
                    sanitizeValue(conjunta.duracaoP75),
                    sanitizeValue(conjunta.duracaoP90),
                    ...(hasDataRefFull
                        ? [
                            formatDataDeDias(conjunta.duracaoMedia, dataReferencia) || '',
                            formatDataDeDias(conjunta.duracaoP10, dataReferencia) || '',
                            formatDataDeDias(conjunta.duracaoP50, dataReferencia) || '',
                            formatDataDeDias(conjunta.duracaoP75, dataReferencia) || '',
                            formatDataDeDias(conjunta.duracaoP90, dataReferencia) || ''
                        ]
                        : [])
                ]);
                ws.getRow(1).font = { bold: true };
                ws.columns.forEach(column => { column.width = 25; });
            }

            // Simulação - Gráficos (TRL + CRL + Conjunta)
            if (simulationCharts.length > 0) {
                const chartWs = workbook.addWorksheet('Simulação - Gráficos');
                let currentRow = 2;
                for (const [index, chart] of simulationCharts.entries()) {
                    if (chart) {
                        const title = chart.title || `Simulação ${index + 1}`;
                        chartWs.getCell(`A${currentRow}`).value = title;
                        chartWs.getCell(`A${currentRow}`).font = { bold: true, size: 14 };
                        currentRow += 2;
                        const imageAdded = await addChartToWorksheet(workbook, chartWs, chart, title, currentRow);
                        if (imageAdded) currentRow += 20;
                    }
                }
                chartWs.getColumn(1).width = 80;
            }
        }

        return workbook;
    };

    // Off-screen rendering function for components
    const renderComponentOffScreen = async (Component, data, forceMode = null, extraProps = {}) => {
        return new Promise(async (resolve) => {
            // Create hidden container
            const container = document.createElement('div');
            container.style.position = 'absolute';
            container.style.left = '-9999px';
            container.style.top = '-9999px';
            container.style.width = '800px';
            container.style.height = '600px';
            container.style.visibility = 'hidden';
            container.style.pointerEvents = 'none';
            container.style.backgroundColor = 'white';
            document.body.appendChild(container);

            try {
                // Create React root and render component
                const root = ReactDOM.createRoot(container);
                root.render(<Component data={data} {...extraProps} />);

                // Wait for component to render (reduced timeout)
                await new Promise(resolve => setTimeout(resolve, 400));

                // Force specific view mode if needed
                if (forceMode) {
                    const select = container.querySelector('select');
                    if (select) {
                        // Change select value programmatically
                        select.value = forceMode;
                        const event = new Event('change', { bubbles: true });
                        select.dispatchEvent(event);

                        // Wait for mode change to take effect (reduced timeout)
                        await new Promise(resolve => setTimeout(resolve, 200));
                    }
                }

                // Wait a bit more for charts to fully render (reduced timeout)
                await new Promise(resolve => setTimeout(resolve, 300));

                // Capture all charts from the container
                const capturedCharts = [];

                // Find Recharts containers
                const rechartsContainers = container.querySelectorAll('.recharts-responsive-container');
                for (const rechartContainer of rechartsContainers) {
                    const rect = rechartContainer.getBoundingClientRect();
                    if (rect.width > 50 && rect.height > 50) {
                        // Try to find chart title from nearby elements
                        let chartTitle = 'Gráfico';

                        // Look for title in parent elements or siblings
                        const parentCard = rechartContainer.closest('.card, .bg-white, .rounded-lg');
                        if (parentCard) {
                            // Look for h2, h3, h4, h5, h6 or elements with title/heading classes
                            const titleElement = parentCard.querySelector('h2, h3, h4, h5, h6, .card-title, .title, [class*="title"], [class*="heading"]');
                            if (titleElement && titleElement.textContent.trim()) {
                                chartTitle = titleElement.textContent.trim();
                            }
                        }

                        // Also check for the specific chart container
                        const chartContainer = rechartContainer.closest('[class*="rounded-lg"], [class*="p-6"]');
                        if (chartContainer && chartTitle === 'Gráfico') {
                            const titleInContainer = chartContainer.querySelector('h3');
                            if (titleInContainer && titleInContainer.textContent.trim()) {
                                chartTitle = titleInContainer.textContent.trim();
                            }
                        }


                        // Alternative: look for legend or any text element that might be a title
                        if (chartTitle === 'Gráfico') {
                            // First, check for SVG title element
                            const svgTitleElement = rechartContainer.querySelector('svg title');
                            if (svgTitleElement && svgTitleElement.textContent.trim()) {
                                chartTitle = svgTitleElement.textContent.trim();
                            } else {
                                // Look for text elements that seem like titles
                                const textElements = rechartContainer.querySelectorAll('text');
                                for (const textEl of textElements) {
                                    const text = textEl.textContent.trim();
                                    // Look for text that seems like a title (longer than axis labels, not numbers)
                                    if (text.length > 5 && !text.match(/^\d+(\.\d+)?$/) && !text.includes('%') && !text.includes('TRL') && !text.includes('CRL')) {
                                        chartTitle = text;
                                        break;
                                    }
                                }

                                // If still no title, try to infer from chart structure
                                if (chartTitle === 'Gráfico') {
                                    const bars = rechartContainer.querySelectorAll('.recharts-bar');
                                    const lines = rechartContainer.querySelectorAll('.recharts-line');

                                    if (bars.length > 0) {
                                        chartTitle = 'Gráfico de Barras';
                                    } else if (lines.length > 0) {
                                        chartTitle = 'Gráfico de Linhas';
                                    }
                                }
                            }
                        }

                        const chart = await captureSVGChart(rechartContainer, chartTitle);
                        if (chart) {
                            capturedCharts.push(chart);
                        }
                    }
                }

                // Find standalone SVGs
                const svgs = container.querySelectorAll('svg');
                for (const svg of svgs) {
                    const rect = svg.getBoundingClientRect();
                    const isInsideRecharts = svg.closest('.recharts-responsive-container');

                    if (rect.width > 100 && rect.height > 100 && !isInsideRecharts) {
                        // Try to find title for standalone SVGs
                        let svgTitle = '';

                        const parentCard = svg.closest('.card, .bg-white');
                        if (parentCard) {
                            const titleElement = parentCard.querySelector('h2, h3, h4, h5, h6, .card-title, .title, [class*="title"], [class*="heading"]');
                            if (titleElement && titleElement.textContent.trim()) {
                                svgTitle = titleElement.textContent.trim();
                            }
                        }

                        const chart = await captureSVGChart(svg, svgTitle);
                        if (chart) {
                            capturedCharts.push(chart);
                        }
                    }
                }

                // Cleanup
                root.unmount();
                document.body.removeChild(container);

                resolve(capturedCharts);

            } catch (error) {
                console.error('Error in off-screen rendering:', error);
                // Cleanup on error
                try {
                    document.body.removeChild(container);
                } catch (cleanupError) {
                    console.error('Cleanup error:', cleanupError);
                }
                resolve([]);
            }
        });
    };

    // New SVG-based chart capture - much faster and better quality
    const captureSVGChart = async (element, title = '') => {
        try {
            // Find SVG element within the container
            let svgElement = element;
            let parentContainer = element;

            if (element.tagName !== 'svg') {
                svgElement = element.querySelector('svg');
                parentContainer = element;
                if (!svgElement) {
                    console.warn(`No SVG found in element for ${title}`);
                    return null;
                }
            }

            // Clone the SVG to avoid modifying the original
            const clonedSVG = svgElement.cloneNode(true);

            // Ensure the SVG has proper dimensions
            const rect = svgElement.getBoundingClientRect();
            clonedSVG.setAttribute('width', rect.width);
            clonedSVG.setAttribute('height', rect.height);

            // Find and capture custom legends (like CustomLegend component)
            const chartContainer = parentContainer.closest('[class*="rounded-lg"], [class*="p-6"], .bg-white');
            let legendInfo = null;

            if (chartContainer) {
                // Look for absolutely positioned legend elements
                const legendElements = chartContainer.querySelectorAll('[style*="position: absolute"]');
                for (const legend of legendElements) {
                    const legendRect = legend.getBoundingClientRect();
                    if (legendRect.width > 10 && legendRect.height > 10) {
                        // Capture legend text content and styling
                        const legendTexts = Array.from(legend.querySelectorAll('div')).map(div => ({
                            text: div.textContent.trim(),
                            color: div.style.color || '#000'
                        })).filter(item => item.text.length > 0);

                        if (legendTexts.length > 0) {
                            // Calculate position relative to SVG, placing legend in top-right corner
                            const svgRight = rect.width;
                            const maxTextWidth = Math.max(...legendTexts.map(t => t.text.length)) * 8; // Estimate text width

                            legendInfo = {
                                texts: legendTexts,
                                position: {
                                    x: svgRight - maxTextWidth - 20, // 20px margin from right
                                    y: 30 // 30px from top
                                }
                            };
                            break;
                        }
                    }
                }
            }

            // Add white background if not present
            if (!clonedSVG.style.backgroundColor && !clonedSVG.getAttribute('style')?.includes('background')) {
                const backgroundRect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
                backgroundRect.setAttribute('width', '100%');
                backgroundRect.setAttribute('height', '100%');
                backgroundRect.setAttribute('fill', 'white');
                clonedSVG.insertBefore(backgroundRect, clonedSVG.firstChild);
            }

            // Add legend to SVG if found
            if (legendInfo) {
                const legendGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
                legendGroup.setAttribute('transform', `translate(${legendInfo.position.x}, ${legendInfo.position.y})`);

                legendInfo.texts.forEach((item, index) => {
                    const textElement = document.createElementNS('http://www.w3.org/2000/svg', 'text');
                    textElement.setAttribute('x', '0');
                    textElement.setAttribute('y', index * 16);
                    textElement.setAttribute('font-family', 'Arial, sans-serif');
                    textElement.setAttribute('font-size', '12');
                    textElement.setAttribute('fill', item.color);
                    textElement.textContent = item.text;
                    legendGroup.appendChild(textElement);
                });

                clonedSVG.appendChild(legendGroup);
            }

            // Convert to data URL for embedding in PDF
            const svgData = new XMLSerializer().serializeToString(clonedSVG);
            const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
            const svgUrl = URL.createObjectURL(svgBlob);

            // Convert SVG to canvas for PDF embedding
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            const img = new Image();

            return new Promise((resolve) => {
                img.onload = () => {
                    // Reduced scale: 1.5x instead of 2x for better performance
                    canvas.width = rect.width * 1.5;
                    canvas.height = rect.height * 1.5;

                    // Fill with white background
                    ctx.fillStyle = 'white';
                    ctx.fillRect(0, 0, canvas.width, canvas.height);

                    // Draw the image
                    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

                    URL.revokeObjectURL(svgUrl);

                    // Reduced quality: 0.75 instead of 0.95 for smaller file size
                    resolve({
                        dataUrl: canvas.toDataURL('image/png', 0.75),
                        width: canvas.width,
                        height: canvas.height,
                        title: title
                    });
                };

                img.onerror = () => {
                    console.error(`Error loading SVG image for ${title}`);
                    URL.revokeObjectURL(svgUrl);
                    resolve(null);
                };

                img.src = svgUrl;
            });

        } catch (error) {
            console.error(`Error capturing SVG chart ${title}:`, error);
            return null;
        }
    };

    const convertToPDF = async (data) => {
        // 16:9 format in landscape (slide style)
        // Standard 16:9 dimensions: 297mm x 167mm (landscape)
        const pdf = new jsPDF('l', 'mm', [297, 167]);
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const margin = 15;
        const headerHeight = 12;
        const footerHeight = 13;
        let yPosition = margin + headerHeight;
        let currentSection = ''; // Track current section for header

        // Load images for footer in parallel
        let imageLeft = null;
        let imageRight = null;

        // Load both images in parallel for better performance
        const [leftResult, rightResult] = await Promise.allSettled([
            fetch('/image.png')
                .then(res => res.blob())
                .then(blob => new Promise((resolve) => {
                    const reader = new FileReader();
                    reader.onloadend = () => resolve(reader.result);
                    reader.readAsDataURL(blob);
                })),
            fetch('/logo_labrisco.png')
                .then(res => res.blob())
                .then(blob => new Promise((resolve) => {
                    const reader = new FileReader();
                    reader.onloadend = () => resolve(reader.result);
                    reader.readAsDataURL(blob);
                }))
        ]);

        if (leftResult.status === 'fulfilled') {
            imageLeft = leftResult.value;
        } else {
            console.error('Error loading image.png:', leftResult.reason);
        }

        if (rightResult.status === 'fulfilled') {
            imageRight = rightResult.value;
        } else {
            console.error('Error loading logo_labrisco.png:', rightResult.reason);
        }

        // Helper function to add header and footer
        const addHeaderAndFooter = () => {
            const currentPage = pdf.internal.getCurrentPageInfo().pageNumber;

            // Header - Section name (no background)
            if (currentSection) {
                // Section name in dark blue
                pdf.setFontSize(16);
                pdf.setFont(undefined, 'bold');
                pdf.setTextColor(0, 59, 107); // Dark blue text (#003B6B)
                pdf.text(currentSection, pageWidth / 2, 8.5, { align: 'center' });
                pdf.setTextColor(0, 0, 0); // Reset color
            }

            // Footer - Background bar (slide style, matching header)
            pdf.setFillColor(0, 59, 107); // #003B6B color
            pdf.rect(0, pageHeight - footerHeight, pageWidth, footerHeight, 'F');

            // Footer - Left image (image.png) - centered vertically
            if (imageLeft) {
                try {
                    const imgHeight = 10;
                    const imgWidth = 26;
                    const imgY = pageHeight - footerHeight + (footerHeight - imgHeight) / 2; // Center vertically
                    pdf.addImage(imageLeft, 'PNG', margin, imgY, imgWidth, imgHeight);
                } catch (error) {
                    console.error('Error adding left footer image:', error);
                }
            }

            // Footer - Right image (logo_labrisco.png) - centered vertically
            if (imageRight) {
                try {
                    const imgHeight = 10;
                    const imgWidth = 26;
                    const imgY = pageHeight - footerHeight + (footerHeight - imgHeight) / 2; // Center vertically
                    pdf.addImage(imageRight, 'PNG', pageWidth - margin - imgWidth, imgY, imgWidth, imgHeight);
                } catch (error) {
                    console.error('Error adding right footer image:', error);
                }
            }

            // Footer - Page number (center) in white to match header style
            pdf.setFontSize(9);
            pdf.setFont(undefined, 'normal');
            pdf.setTextColor(255, 255, 255); // White text to match header
            pdf.text(`${currentPage}`, pageWidth / 2, pageHeight - 6, { align: 'center' });
            pdf.setTextColor(0, 0, 0); // Reset color
        };

        // Track if header was added to current page
        let headerAddedToPage = false;

        // Helper function to ensure header is added to current page
        const ensureHeaderOnPage = () => {
            if (!headerAddedToPage) {
                addHeaderAndFooter();
                headerAddedToPage = true;
            }
        };

        // Helper function to add a new page if needed
        const checkAndAddPage = (requiredSpace) => {
            if (yPosition + requiredSpace > pageHeight - margin - footerHeight) {
                ensureHeaderOnPage(); // Ensure header on current page before leaving
                pdf.addPage();
                yPosition = margin + headerHeight;
                headerAddedToPage = false; // Reset flag for new page
                return true;
            }
            return false;
        };

        // Title detection is now handled directly in off-screen rendering
        // Section titles are shown in the header instead of in the body

        // Helper function to add chart to PDF (optimized for 16:9)
        const addChartToPDF = (chartData, title = '', detectedTitle = '', isFirstInSection = false) => {
            if (!chartData) return;

            // Use detected title if available, otherwise use provided title
            const finalTitle = detectedTitle || title;

            // Calculate chart dimensions - 70% width, height limited
            const maxChartWidth = (pageWidth - 2 * margin) * 0.8;
            const maxChartHeight = (pageHeight - margin - headerHeight - footerHeight) * 0.9;

            const aspectRatio = chartData.height / chartData.width;
            let imgWidth = maxChartWidth;
            let imgHeight = maxChartWidth * aspectRatio;

            // If height exceeds limit, scale down based on height
            if (imgHeight > maxChartHeight) {
                imgHeight = maxChartHeight;
                imgWidth = maxChartHeight / aspectRatio;
            }

            // Center horizontally
            const imgX = (pageWidth - imgWidth) / 2;

            const titleHeight = finalTitle ? 10 : 0; // 9 (font) + 1 (spacing)
            const totalHeight = titleHeight + imgHeight + 5; // 5 is bottom spacing

            // Check if title + chart fit together, if not add new page
            // IMPORTANT: Skip this check if this is the first chart in a new section
            // because we just started a fresh page/section
            if (!isFirstInSection && yPosition + totalHeight > pageHeight - margin - footerHeight - 5) {
                ensureHeaderOnPage(); // Ensure header on current page before leaving
                pdf.addPage();
                yPosition = margin + headerHeight + 3;
                headerAddedToPage = false; // Reset flag for new page
            }

            // Ensure header is on current page
            ensureHeaderOnPage();

            // Now add title and chart together (guaranteed to be on same page)
            if (finalTitle) {
                pdf.setFontSize(9);
                pdf.setFont(undefined, 'bold');
                pdf.text(finalTitle.replaceAll("→", "->"), pageWidth / 2, yPosition, { align: 'center' });
                yPosition += 4;
            }

            // Add chart centered
            pdf.addImage(chartData.dataUrl, 'PNG', imgX, yPosition, imgWidth, imgHeight);
            yPosition += imgHeight + 5;
        };

        // Main Title
        pdf.setFontSize(20);
        pdf.setFont(undefined, 'bold');
        // pdf.text('DInoUSP - Gráficos de Análise', pageWidth / 2, yPosition, { align: 'center' });
        yPosition += 15;

        // Date
        pdf.setFontSize(10);
        pdf.setFont(undefined, 'normal');
        const timestamp = new Date().toLocaleString('pt-BR');
        // pdf.text(`Gerado em: ${timestamp}`, pageWidth / 2, yPosition, { align: 'center' });
        yPosition += 20;

        // Add Filters Information (Compact version for 16:9)
        const filtersInfo = formatAppliedFilters();
        if (filtersInfo) {
            // Title
            pdf.setFontSize(14);
            pdf.setFont(undefined, 'bold');
            pdf.text('Filtros Aplicados', margin, yPosition);
            yPosition += 8;

            // Parameters in compact format
            pdf.setFontSize(9);
            pdf.setFont(undefined, 'bold');
            pdf.text('PARÂMETROS:', margin, yPosition);
            pdf.setFont(undefined, 'normal');
            pdf.text(`TRL: ${filtersInfo.parametros.TRL.inicial}-${filtersInfo.parametros.TRL.final} | CRL: ${filtersInfo.parametros.CRL.inicial}-${filtersInfo.parametros.CRL.final} | Tempo: ${filtersInfo.parametros.tempo} | Custo: ${filtersInfo.parametros.custo}`, margin + 50, yPosition);
            yPosition += 6;

            // Two-column layout for filters
            const col1X = margin;
            const col2X = pageWidth / 2 + 10;
            let col1Y = yPosition;
            let col2Y = yPosition;

            // Helper to add filter section in compact format
            const addCompactFilter = (title, items, useCol2 = false) => {
                if (!items || items.length === 0) return;

                const x = useCol2 ? col2X : col1X;
                let y = useCol2 ? col2Y : col1Y;

                pdf.setFontSize(9);
                pdf.setFont(undefined, 'bold');
                pdf.text(`${title}:`, x, y);
                y += 5;

                pdf.setFont(undefined, 'normal');
                pdf.setFontSize(8);
                const itemsText = items.join(', ');
                const lines = pdf.splitTextToSize(itemsText, (pageWidth / 2) - 30);
                lines.forEach(line => {
                    pdf.text(line, x + 3, y);
                    y += 4;
                });
                y += 3;

                if (useCol2) {
                    col2Y = y;
                } else {
                    col1Y = y;
                }
            };

            // Add filters in two columns
            addCompactFilter('Grau Inovação', filtersInfo.grauInovacao?.map(String), false);
            addCompactFilter('Tipologia', filtersInfo.tipologia, false);
            addCompactFilter('Status', filtersInfo.status, false);

            addCompactFilter('Portfolio', filtersInfo.portfolio, true);
            addCompactFilter('Iniciativa Tec.', filtersInfo.iniciativaTecnologica, true);

            // Date filters
            if (filtersInfo.dataMin || filtersInfo.dataMax) {
                const dateText = [];
                if (filtersInfo.dataMin) dateText.push(`Min: ${filtersInfo.dataMin}`);
                if (filtersInfo.dataMax) dateText.push(`Max: ${filtersInfo.dataMax}`);
                addCompactFilter('Datas', dateText, true);
            }

            // Update yPosition to the maximum of both columns
            yPosition = Math.max(col1Y, col2Y) + 5;

            // Force new page after filters to keep filters page isolated
            ensureHeaderOnPage(); // Ensure header on filters page
        }

        // Set initial section and add header to first page (filters page)
        currentSection = 'Filtros Aplicados';
        headerAddedToPage = false;
        ensureHeaderOnPage();

        try {
            // Off-screen rendering approach - render each selected section independently

            // 1. RESUMO - Render off-screen
            if (selectedSections.includes('resumo') && data.Resumo) {
                const resumoCharts = await renderComponentOffScreen(ResumoTab, { Resumo: data.Resumo }, 'TRL/CRL');

                // Only create new page if we have charts to add
                if (resumoCharts && resumoCharts.length > 0) {
                    // Change section - always add new page to keep sections isolated
                    ensureHeaderOnPage(); // Finalize previous page
                    currentSection = 'Resumo';
                    pdf.addPage();
                    yPosition = margin + headerHeight + 3;
                    headerAddedToPage = false; // Reset for new page

                    resumoCharts.forEach((chart, i) => {
                        if (chart) {
                            addChartToPDF(chart, `Resumo - Gráfico ${i + 1}`, chart.title, i === 0);
                        }
                    });
                }
            }

            // 2. ESTATÍSTICAS - Render off-screen in graphics mode
            if (selectedSections.includes('statistics') && data.Estatisticas) {
                const statisticsCharts = await renderComponentOffScreen(StatisticsTab, { Estatisticas: data.Estatisticas }, 'GRAFICOS');

                // Only create new page if we have charts to add
                if (statisticsCharts && statisticsCharts.length > 0) {
                    // Change section - always add new page to keep sections isolated
                    ensureHeaderOnPage(); // Finalize previous page
                    currentSection = 'Estatísticas Descritivas';
                    pdf.addPage();
                    yPosition = margin + headerHeight + 3;
                    headerAddedToPage = false; // Reset for new page

                    statisticsCharts.forEach((chart, i) => {
                        if (chart) {
                            addChartToPDF(chart, `Estatística ${i + 1}`, chart.title, i === 0);
                        }
                    });
                }
            }

            // 3. DISTRIBUIÇÕES - Render each mode off-screen
            if (selectedSections.includes('distributions') && data.Distribuicoes) {
                // Render all distribution types first
                const empiricalCharts = await renderComponentOffScreen(DistributionsTab, { Distribuicoes: data.Distribuicoes }, 'EMPÍRICAS');
                const fittedCharts = await renderComponentOffScreen(DistributionsTab, { Distribuicoes: data.Distribuicoes }, 'AJUSTADAS');
                const rfCharts = await renderComponentOffScreen(DistributionsTab, { Distribuicoes: data.Distribuicoes }, 'RANDOM FOREST');

                const totalCharts = (empiricalCharts?.length || 0) + (fittedCharts?.length || 0) + (rfCharts?.length || 0);

                // Only create new page if we have charts to add
                if (totalCharts > 0) {
                    // Change section - always add new page to keep sections isolated
                    ensureHeaderOnPage(); // Finalize previous page
                    currentSection = 'Distribuições';
                    pdf.addPage();
                    yPosition = margin + headerHeight + 3;
                    headerAddedToPage = false; // Reset for new page

                    let chartIndex = 0;
                    // Add empirical distributions
                    empiricalCharts?.forEach((chart, i) => {
                        if (chart) {
                            addChartToPDF(chart, `Distribuição Empírica ${i + 1}`, chart.title, chartIndex === 0);
                            chartIndex++;
                        }
                    });

                    // Add fitted distributions
                    fittedCharts?.forEach((chart, i) => {
                        if (chart) {
                            addChartToPDF(chart, `Distribuição Ajustada ${i + 1}`, chart.title, false);
                        }
                    });

                    // Add Random Forest
                    rfCharts?.forEach((chart, i) => {
                        if (chart) {
                            addChartToPDF(chart, `Random Forest ${i + 1}`, chart.title, false);
                        }
                    });
                }
            }

            // 4. SIMULAÇÃO - Render off-screen in graphics mode
            if (selectedSections.includes('simulation') && data.Simulador) {
                const simulationCharts = await renderComponentOffScreen(SimulationTab, { Simulador: data.Simulador }, 'GRAFICOS', { dataReferencia });

                // Only create new page if we have charts to add
                if (simulationCharts && simulationCharts.length > 0) {
                    // Change section - always add new page to keep sections isolated
                    ensureHeaderOnPage(); // Finalize previous page
                    currentSection = 'Simulação';
                    pdf.addPage();
                    yPosition = margin + headerHeight + 3;
                    headerAddedToPage = false; // Reset for new page

                    simulationCharts.forEach((chart, i) => {
                        if (chart) {
                            addChartToPDF(chart, `Simulação ${i + 1}`, chart.title, i === 0);
                        }
                    });
                }
            }

            // Ensure header/footer on last page
            ensureHeaderOnPage();


        } catch (error) {
            console.error('Error generating PDF:', error);
            throw error;
        }

        return pdf;
    };

    const handleExport = async () => {
        if (selectedSections.length === 0) {
            alert('Selecione pelo menos uma seção para exportar.');
            return;
        }

        setIsExporting(true);
        setExportSuccess(false);

        try {
            const exportData = prepareExportData();
            const timestamp = new Date().toISOString().slice(0, 10);

            if (selectedFormat === 'json') {
                const dataStr = JSON.stringify(exportData, null, 2);
                const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);

                const linkElement = document.createElement('a');
                linkElement.setAttribute('href', dataUri);
                linkElement.setAttribute('download', `dino_${timestamp}.json`);
                linkElement.click();

            } else if (selectedFormat === 'xlsx') {
                const workbook = await convertToXLSXWithCharts(exportData);
                const buffer = await workbook.xlsx.writeBuffer();
                const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                const url = window.URL.createObjectURL(blob);

                const linkElement = document.createElement('a');
                linkElement.setAttribute('href', url);
                linkElement.setAttribute('download', `dino_${timestamp}.xlsx`);
                linkElement.click();

                window.URL.revokeObjectURL(url);
            } else if (selectedFormat === 'pdf') {
                const pdf = await convertToPDF(exportData);
                pdf.save(`dino_${timestamp}.pdf`);
            }

            setExportSuccess(true);
            setTimeout(() => {
                onClose();
            }, 2000);

        } catch (error) {
            alert('Erro ao exportar dados. Tente novamente.');
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-slate-700 to-slate-600 text-white">
                    <h3 className="text-lg font-semibold">Exportar resultados</h3>
                    <button
                        onClick={onClose}
                        disabled={isExporting}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <HiX className="w-6 h-6" />
                    </button>
                </div>

                <div className="p-6 space-y-6">
                    <div>
                        <h4 className="text-sm font-medium text-gray-900 mb-3">Selecione o formato de exportação:</h4>
                        <div className="relative">
                            <select
                                value={selectedFormat}
                                onChange={(e) => setSelectedFormat(e.target.value)}
                                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 bg-white text-sm"
                            >
                                {formats.map((format) => (
                                    <option key={format.id} value={format.id}>
                                        {format.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div>
                        <h4 className="text-sm font-medium text-gray-900 mb-3">Quais resultados deseja exportar?</h4>
                        <div className="space-y-2">
                            {sections.map((section) => (
                                <label key={section.id} className="flex items-start space-x-3 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={selectedSections.includes(section.id)}
                                        onChange={() => handleSectionChange(section.id)}
                                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mt-0.5"
                                    />
                                    <div>
                                        <div className="text-sm font-medium text-gray-900">{section.label}</div>
                                        <div className="text-xs text-gray-500">{section.description}</div>
                                    </div>
                                </label>
                            ))}
                        </div>
                    </div>

                </div>

                <div className="flex flex-wrap items-center justify-center gap-3 p-6 border-gray-200">
                    <button
                        onClick={onClose}
                        disabled={isExporting}
                        className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors disabled:opacity-50"
                    >
                        Voltar
                    </button>
                    <button
                        onClick={handleExport}
                        disabled={isExporting || exportSuccess || selectedSections.length === 0}
                        style={{ backgroundColor: "#003B6B" }}
                        className="flex items-center justify-center space-x-2 px-8 py-2 text-sm font-medium text-white rounded-md transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90"
                    >
                        {isExporting ? (
                            <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                <span>Exportando...</span>
                            </>
                        ) : exportSuccess ? (
                            <>
                                <HiCheck className="w-4 h-4" />
                                <span>Exportado!</span>
                            </>
                        ) : (
                            <>
                                <HiDownload className="w-4 h-4" />
                                <span>Exportar</span>
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ExportModal;
